import { Platform } from 'react-native';

/**
 * App Constants
 */

// Server URL defaults
const PRODUCTION_API_URL = 'https://app.fishmemory.net';
const PRODUCTION_WS_URL = 'wss://app.fishmemory.net/ws';

function getLocalWebOrigin(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const origin = normalizeServerUrl(window.location?.origin);
  if (!origin) return null;
  try {
    const { hostname } = new URL(origin);
    return ['localhost', '127.0.0.1', '::1'].includes(hostname) ? origin : null;
  } catch {
    return null;
  }
}

const LOCAL_WEB_ORIGIN = getLocalWebOrigin();
const NATIVE_API_URL = process.env.EXPO_PUBLIC_API_URL || PRODUCTION_API_URL;
const WEB_API_URL = LOCAL_WEB_ORIGIN || process.env.EXPO_PUBLIC_WEB_API_URL || process.env.EXPO_PUBLIC_API_URL || PRODUCTION_API_URL;

export const DEFAULT_API_URL = Platform.OS === 'web' ? WEB_API_URL : NATIVE_API_URL;
export const DEFAULT_WS_URL = Platform.OS === 'web'
  ? (LOCAL_WEB_ORIGIN ? `${LOCAL_WEB_ORIGIN.replace(/^http/i, 'ws')}/ws` : process.env.EXPO_PUBLIC_WEB_WS_URL || process.env.EXPO_PUBLIC_WS_URL || PRODUCTION_WS_URL)
  : (process.env.EXPO_PUBLIC_WS_URL || PRODUCTION_WS_URL);

// Mutable override — set from storage at startup or settings
let _serverUrlOverride: string | null = null;

function tryNormalizeServerUrl(url: string | null | undefined): string | null {
  const raw = String(url || '').trim();
  if (!raw) return null;

  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  try {
    const parsed = new URL(withProtocol);
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    parsed.pathname = '';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

export function normalizeServerUrl(url: string | null | undefined): string | null {
  return tryNormalizeServerUrl(url);
}

export function isValidServerUrl(url: string | null | undefined): boolean {
  return !!tryNormalizeServerUrl(url);
}

export function setServerUrlOverride(url: string | null): void {
  _serverUrlOverride = tryNormalizeServerUrl(url);
}

export function getApiBaseUrl(): string {
  if (_serverUrlOverride) return _serverUrlOverride;
  return DEFAULT_API_URL;
}

export function getWsUrl(): string {
  if (_serverUrlOverride) {
    const wsBase = _serverUrlOverride.replace(/^http/i, 'ws');
    return `${wsBase}/ws`;
  }
  return DEFAULT_WS_URL;
}

// App
export const APP_VERSION = '1.0.0';
export const CHAT_PAGE_SIZE = 50;
export const REMINDER_PAGE_SIZE = 50;

// Languages — fetched from API, with static fallback
export interface Language {
  code: string;
  label: string;       // native name
  englishName?: string; // English name for search
  flag: string;
  rtl: boolean;
  tier?: string;
}

export const DEFAULT_LANGUAGES: Language[] = [
  // Core
  { code: 'en', label: 'English', flag: '🇬🇧', rtl: false, tier: 'core' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', rtl: true, tier: 'core' },
  { code: 'lb', label: 'Lebnéné', flag: '🇱🇧', rtl: false, tier: 'core' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', rtl: false, tier: 'core' },
  // Launch
  { code: 'zh', label: '中文', flag: '🇨🇳', rtl: false, tier: 'launch' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳', rtl: false, tier: 'launch' },
  { code: 'es', label: 'Español', flag: '🇪🇸', rtl: false, tier: 'launch' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩', rtl: false, tier: 'launch' },
  { code: 'pt', label: 'Português', flag: '🇧🇷', rtl: false, tier: 'launch' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺', rtl: false, tier: 'launch' },
  { code: 'ja', label: '日本語', flag: '🇯🇵', rtl: false, tier: 'launch' },
  // Community
  { code: 'af', label: 'Afrikaans', flag: '🇿🇦', rtl: false, tier: 'community' },
  { code: 'am', label: 'አማርኛ', flag: '🇪🇹', rtl: false, tier: 'community' },
  { code: 'as', label: 'অসমীয়া', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'az', label: 'Azərbaycanca', flag: '🇦🇿', rtl: false, tier: 'community' },
  { code: 'be', label: 'Беларуская', flag: '🇧🇾', rtl: false, tier: 'community' },
  { code: 'bg', label: 'Български', flag: '🇧🇬', rtl: false, tier: 'community' },
  { code: 'bs', label: 'Bosanski', flag: '🇧🇦', rtl: false, tier: 'community' },
  { code: 'ca', label: 'Català', flag: '🇦🇩', rtl: false, tier: 'community' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿', rtl: false, tier: 'community' },
  { code: 'cy', label: 'Cymraeg', flag: '🏴', rtl: false, tier: 'community' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰', rtl: false, tier: 'community' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', rtl: false, tier: 'community' },
  { code: 'dv', label: 'ދިވެހި', flag: '🇲🇻', rtl: true, tier: 'community' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷', rtl: false, tier: 'community' },
  { code: 'eo', label: 'Esperanto', flag: '🌍', rtl: false, tier: 'community' },
  { code: 'et', label: 'Eesti', flag: '🇪🇪', rtl: false, tier: 'community' },
  { code: 'eu', label: 'Euskara', flag: '🇪🇸', rtl: false, tier: 'community' },
  { code: 'fa', label: 'فارسی', flag: '🇮🇷', rtl: true, tier: 'community' },
  { code: 'fi', label: 'Suomi', flag: '🇫🇮', rtl: false, tier: 'community' },
  { code: 'fy', label: 'Frysk', flag: '🇳🇱', rtl: false, tier: 'community' },
  { code: 'ga', label: 'Gaeilge', flag: '🇮🇪', rtl: false, tier: 'community' },
  { code: 'gd', label: 'Gàidhlig', flag: '🏴', rtl: false, tier: 'community' },
  { code: 'gl', label: 'Galego', flag: '🇪🇸', rtl: false, tier: 'community' },
  { code: 'gu', label: 'ગુજરાતી', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'ha', label: 'Hausa', flag: '🇳🇬', rtl: false, tier: 'community' },
  { code: 'he', label: 'עברית', flag: '🇮🇱', rtl: true, tier: 'community' },
  { code: 'hr', label: 'Hrvatski', flag: '🇭🇷', rtl: false, tier: 'community' },
  { code: 'ht', label: 'Kreyòl Ayisyen', flag: '🇭🇹', rtl: false, tier: 'community' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺', rtl: false, tier: 'community' },
  { code: 'hy', label: 'Հայերեն', flag: '🇦🇲', rtl: false, tier: 'community' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩', rtl: false, tier: 'community' },
  { code: 'ig', label: 'Igbo', flag: '🇳🇬', rtl: false, tier: 'community' },
  { code: 'is', label: 'Íslenska', flag: '🇮🇸', rtl: false, tier: 'community' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', rtl: false, tier: 'community' },
  { code: 'jv', label: 'Basa Jawa', flag: '🇮🇩', rtl: false, tier: 'community' },
  { code: 'ka', label: 'ქართული', flag: '🇬🇪', rtl: false, tier: 'community' },
  { code: 'kk', label: 'Қазақша', flag: '🇰🇿', rtl: false, tier: 'community' },
  { code: 'km', label: 'ខ្មែរ', flag: '🇰🇭', rtl: false, tier: 'community' },
  { code: 'kn', label: 'ಕನ್ನಡ', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'ko', label: '한국어', flag: '🇰🇷', rtl: false, tier: 'community' },
  { code: 'ku', label: 'Kurdî', flag: '🇮🇶', rtl: false, tier: 'community' },
  { code: 'ky', label: 'Кыргызча', flag: '🇰🇬', rtl: false, tier: 'community' },
  { code: 'la', label: 'Latīna', flag: '🇻🇦', rtl: false, tier: 'community' },
  { code: 'ln', label: 'Lingála', flag: '🇨🇩', rtl: false, tier: 'community' },
  { code: 'lo', label: 'ລາວ', flag: '🇱🇦', rtl: false, tier: 'community' },
  { code: 'lt', label: 'Lietuvių', flag: '🇱🇹', rtl: false, tier: 'community' },
  { code: 'lv', label: 'Latviešu', flag: '🇱🇻', rtl: false, tier: 'community' },
  { code: 'mg', label: 'Malagasy', flag: '🇲🇬', rtl: false, tier: 'community' },
  { code: 'mi', label: 'Te Reo Māori', flag: '🇳🇿', rtl: false, tier: 'community' },
  { code: 'mk', label: 'Македонски', flag: '🇲🇰', rtl: false, tier: 'community' },
  { code: 'ml', label: 'മലയാളം', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'mn', label: 'Монгол', flag: '🇲🇳', rtl: false, tier: 'community' },
  { code: 'mr', label: 'मराठी', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'ms', label: 'Bahasa Melayu', flag: '🇲🇾', rtl: false, tier: 'community' },
  { code: 'mt', label: 'Malti', flag: '🇲🇹', rtl: false, tier: 'community' },
  { code: 'my', label: 'မြန်မာစာ', flag: '🇲🇲', rtl: false, tier: 'community' },
  { code: 'ne', label: 'नेपाली', flag: '🇳🇵', rtl: false, tier: 'community' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', rtl: false, tier: 'community' },
  { code: 'no', label: 'Norsk', flag: '🇳🇴', rtl: false, tier: 'community' },
  { code: 'ny', label: 'Chichewa', flag: '🇲🇼', rtl: false, tier: 'community' },
  { code: 'or', label: 'ଓଡ଼ିଆ', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱', rtl: false, tier: 'community' },
  { code: 'ps', label: 'پښتو', flag: '🇦🇫', rtl: true, tier: 'community' },
  { code: 'ro', label: 'Română', flag: '🇷🇴', rtl: false, tier: 'community' },
  { code: 'rw', label: 'Ikinyarwanda', flag: '🇷🇼', rtl: false, tier: 'community' },
  { code: 'sd', label: 'سنڌي', flag: '🇵🇰', rtl: true, tier: 'community' },
  { code: 'si', label: 'සිංහල', flag: '🇱🇰', rtl: false, tier: 'community' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰', rtl: false, tier: 'community' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮', rtl: false, tier: 'community' },
  { code: 'sm', label: 'Gagana Samoa', flag: '🇼🇸', rtl: false, tier: 'community' },
  { code: 'sn', label: 'chiShona', flag: '🇿🇼', rtl: false, tier: 'community' },
  { code: 'so', label: 'Soomaali', flag: '🇸🇴', rtl: false, tier: 'community' },
  { code: 'sq', label: 'Shqip', flag: '🇦🇱', rtl: false, tier: 'community' },
  { code: 'sr', label: 'Српски', flag: '🇷🇸', rtl: false, tier: 'community' },
  { code: 'st', label: 'Sesotho', flag: '🇱🇸', rtl: false, tier: 'community' },
  { code: 'su', label: 'Basa Sunda', flag: '🇮🇩', rtl: false, tier: 'community' },
  { code: 'sv', label: 'Svenska', flag: '🇸🇪', rtl: false, tier: 'community' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪', rtl: false, tier: 'community' },
  { code: 'ta', label: 'தமிழ்', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'te', label: 'తెలుగు', flag: '🇮🇳', rtl: false, tier: 'community' },
  { code: 'tg', label: 'Тоҷикӣ', flag: '🇹🇯', rtl: false, tier: 'community' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭', rtl: false, tier: 'community' },
  { code: 'ti', label: 'ትግርኛ', flag: '🇪🇷', rtl: false, tier: 'community' },
  { code: 'tk', label: 'Türkmen', flag: '🇹🇲', rtl: false, tier: 'community' },
  { code: 'tl', label: 'Filipino', flag: '🇵🇭', rtl: false, tier: 'community' },
  { code: 'tn', label: 'Setswana', flag: '🇧🇼', rtl: false, tier: 'community' },
  { code: 'to', label: 'Lea Fakatonga', flag: '🇹🇴', rtl: false, tier: 'community' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', rtl: false, tier: 'community' },
  { code: 'tt', label: 'Татарча', flag: '🇷🇺', rtl: false, tier: 'community' },
  { code: 'ug', label: 'ئۇيغۇرچە', flag: '🇨🇳', rtl: true, tier: 'community' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦', rtl: false, tier: 'community' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰', rtl: true, tier: 'community' },
  { code: 'uz', label: 'Oʻzbekcha', flag: '🇺🇿', rtl: false, tier: 'community' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳', rtl: false, tier: 'community' },
  { code: 'wo', label: 'Wolof', flag: '🇸🇳', rtl: false, tier: 'community' },
  { code: 'xh', label: 'isiXhosa', flag: '🇿🇦', rtl: false, tier: 'community' },
  { code: 'yi', label: 'ייִדיש', flag: '🇮🇱', rtl: true, tier: 'community' },
  { code: 'yo', label: 'Yorùbá', flag: '🇳🇬', rtl: false, tier: 'community' },
  { code: 'zu', label: 'isiZulu', flag: '🇿🇦', rtl: false, tier: 'community' },
];

// English name lookup for search — keyed by language code
export const LANG_ENGLISH_NAMES: Record<string, string> = {
  en: 'English', ar: 'Arabic', lb: 'Lebanese', fr: 'French',
  zh: 'Chinese', hi: 'Hindi', es: 'Spanish', bn: 'Bengali',
  pt: 'Portuguese', ru: 'Russian', ja: 'Japanese',
  af: 'Afrikaans', am: 'Amharic', as: 'Assamese', az: 'Azerbaijani',
  be: 'Belarusian', bg: 'Bulgarian', bs: 'Bosnian', ca: 'Catalan',
  cs: 'Czech', cy: 'Welsh', da: 'Danish', de: 'German',
  dv: 'Divehi', el: 'Greek', eo: 'Esperanto', et: 'Estonian',
  eu: 'Basque', fa: 'Persian', fi: 'Finnish', fy: 'Frisian',
  ga: 'Irish', gd: 'Scottish Gaelic', gl: 'Galician', gu: 'Gujarati',
  ha: 'Hausa', he: 'Hebrew', hr: 'Croatian', ht: 'Haitian Creole',
  hu: 'Hungarian', hy: 'Armenian', id: 'Indonesian', ig: 'Igbo',
  is: 'Icelandic', it: 'Italian', jv: 'Javanese', ka: 'Georgian',
  kk: 'Kazakh', km: 'Khmer', kn: 'Kannada', ko: 'Korean',
  ku: 'Kurdish', ky: 'Kyrgyz', la: 'Latin', ln: 'Lingala',
  lo: 'Lao', lt: 'Lithuanian', lv: 'Latvian', mg: 'Malagasy',
  mi: 'Maori', mk: 'Macedonian', ml: 'Malayalam', mn: 'Mongolian',
  mr: 'Marathi', ms: 'Malay', mt: 'Maltese', my: 'Burmese',
  ne: 'Nepali', nl: 'Dutch', no: 'Norwegian', ny: 'Chichewa',
  or: 'Odia', pa: 'Punjabi', pl: 'Polish', ps: 'Pashto',
  ro: 'Romanian', rw: 'Kinyarwanda', sd: 'Sindhi', si: 'Sinhala',
  sk: 'Slovak', sl: 'Slovenian', sm: 'Samoan', sn: 'Shona',
  so: 'Somali', sq: 'Albanian', sr: 'Serbian', st: 'Sesotho',
  su: 'Sundanese', sv: 'Swedish', sw: 'Swahili', ta: 'Tamil',
  te: 'Telugu', tg: 'Tajik', th: 'Thai', ti: 'Tigrinya',
  tk: 'Turkmen', tl: 'Filipino', tn: 'Tswana', to: 'Tongan',
  tr: 'Turkish', tt: 'Tatar', ug: 'Uyghur', uk: 'Ukrainian',
  ur: 'Urdu', uz: 'Uzbek', vi: 'Vietnamese', wo: 'Wolof',
  xh: 'Xhosa', yi: 'Yiddish', yo: 'Yoruba', zu: 'Zulu',
};

// Mutable languages list — updated from API at startup
let _languages: Language[] = [...DEFAULT_LANGUAGES];

export function getLanguages(): Language[] { return _languages; }
export function setLanguages(langs: Language[]): void { _languages = langs; }
export function isRtlLang(code: string): boolean {
  return _languages.find(l => l.code === code)?.rtl ?? false;
}

// Legacy export for backward compatibility
export const LANGUAGES = DEFAULT_LANGUAGES;

export type LanguageCode = string;

// Theme colors
export const COLORS = {
  primary: '#1565C0',
  primaryLight: '#E3F2FD',
  secondary: '#34a853',
  success: '#34a853',
  warning: '#FF9500',
  error: '#C0392B',
  danger: '#C0392B',
  background: '#F5F8FC',
  surface: '#FFFFFF',
  card: '#FFFFFF',
  text: '#1A202C',
  textPrimary: '#1A202C',
  textSecondary: '#4A5568',
  border: 'rgba(33, 150, 243, 0.12)',
  userBubble: '#1565C0',
  chatBubbleUser: '#1565C0',
  chatBubbleBot: '#FFFFFF',
  tabBar: '#FFFFFF',
  tabBarActive: '#1565C0',
  tabBarInactive: '#94A3B8',
} as const;
